import { backend_api_v2 } from '/src/utils/apiv2'
import bus from '/src/utils/event_bus'
import { defineStore } from 'pinia'
import { useAppStore } from "/src/stores/app"
import { useSelectedStore } from "/src/stores/selected"
import { useTranslationsStore } from './translations'
import vuetify from '/src/plugins/vuetify';
import * as Sentry from "@sentry/vue";
import { hasLogisticsTipos, hasProductionTipos, hasOnlyLogisticsTipos } from '/src/components/fields/tipos.js'
import { kitMode, kitTrackingOn } from '/src/utils/kits.js'


// The token refresh in flight, if any. Outside the state so it is not reactive.
let refreshing = null


function determineUserLanguage(auth) {
    if (auth.multilang && auth.language) {
        return auth.language || "en"
    } else
        return auth.company.meta.language || "en"
}


export const useSessionStore = defineStore('session', {
    state: () => ({
        access_token: localStorage.getItem('access_token'),
        refresh_token: localStorage.getItem('refresh_token'),
        authenticated: !!localStorage.getItem('access_token'),
        user_object: {
            _flag: "not-initialized",
            username: "",
            auth: {}
        }
    }),
    getters: {
        // Station and line user selection
        selected_station: state => state.user_object.auth?.selected_station_obj,
        selected_line: state => state.user_object.auth?.selected_line_obj,
        selected_station_uuid: state => state.user_object.auth?.selected_production_station,
        selected_line_uuid: state => state.user_object.auth?.selected_production_line,
        selected_station_name() { return this.selected_station?.name || "---" },
        selected_line_name() { return this.selected_line?.name || "---" },
        selected_station_zones() { return this.selected_station?.zones || [] },
        selected_line_tipos() { return this.selected_line?.tipos || [] },

        // Logistics/production tipo classification for the selected line
        pl_has_logistics_tipos() { return hasLogisticsTipos(this.selected_line_tipos) },
        pl_has_production_tipos() { return hasProductionTipos(this.selected_line_tipos) },
        pl_only_logistics_tipos() { return hasOnlyLogisticsTipos(this.selected_line_tipos) },

        // Production employees: who may work under this account, and which of
        // them are working right now. Both arrive inside /my_user/, so the
        // viewer never needs a second request for the picker.
        my_employees: state => state.user_object.auth?.production_employees || [],
        selected_employees: state => state.user_object.auth?.selected_production_employees_obj || [],
        // What the backend will actually stamp: the ticked ones, or the single
        // candidate, or the account itself. Resolved server-side so the button
        // and the records can never disagree.
        effective_employees: state => state.user_object.auth?.effective_production_employees || [],
        selected_employee_ids() { return this.selected_employees.map(e => e.id) },
        // More than one candidate is the only case where there is something to
        // choose, which is what gates the account-menu entry.
        can_change_employees() { return this.my_employees.length > 1 },
        // `JAN · PIET`, or `JAN +3` past two, so every surface that shows the
        // set has the same bounded length.
        selected_employees_label() {
            const aliases = this.effective_employees.map(e => e.alias).filter(Boolean)
            if (aliases.length == 0) return ""
            if (aliases.length <= 2) return aliases.join(" · ")
            return `${aliases[0]} +${aliases.length - 1}`
        },
        // What names the session on screen: who is working, and the username
        // only when there is nobody to name. Printing both would put the same
        // identity on the line twice for the common case, where the account
        // answers for itself under an alias seeded from that very username.
        account_label() {
            return this.selected_employees_label || this.user_object.username || ""
        },

        // Adming defined line and station for the user
        station: state => state.user_object.auth?.production_station_obj,
        line: state => state.user_object.auth?.production_line_obj,
        station_uuid: state => state.user_object.auth?.production_station,
        line_uuid: state => state.user_object.auth?.production_line,
        station_name() { return this.station?.name || "---" },
        line_name() { return this.line?.name || "---" },

        // User company and flags
        my_company: state => state.user_object.auth?.company,
        iam_prod_worker: state => state.user_object.auth?.production_user,
        iam_admin: state => state.user_object.auth?.company_admin,
        androidLicensed: state => state.user_object.auth?.licenses?.includes('android'),
        mobliteLicensed: state => state.user_object.auth?.company?.m_lite,
        // Stored in company.meta by the Company Settings dialog. Absent means
        // shown, which is what every company got before the setting was read;
        // only an explicit `false` hides the button.
        viewer_show_qr: state => state.user_object?.auth?.company?.meta?.viewer_show_qr ?? true,
        viewer_show_rfid: state => state.user_object?.auth?.company?.meta?.viewer_show_rfid ?? true,
        subzones_from_10: state => state.user_object?.auth?.company?.meta?.subzones_from_10 || false,
        // Never ask the OS for a photo, even where `capture` would reach it.
        // Some companies manage their tablets with a device policy that blocks
        // the camera app, and nothing in the browser can see that: Capacitor
        // asks, the policy refuses, and the operator silently gets the Android
        // photo picker instead of a camera. So the company says it up front and
        // the viewer goes straight to the in-app one.
        //
        // Read here and not from the company store: that one is only loaded by
        // the manager bootstrap, and this setting exists for production users.
        avoid_native_camera: state => !!state.user_object?.auth?.company?.meta?.avoid_native_camera,
        // The viewer list's second line, off at all but three companies. Same
        // reason as above for reading it here: the list is a production-user
        // screen, and the company store is a manager bootstrap.
        custom_list_hint: state => !!state.user_object?.auth?.company?.meta?.custom_list_hint,
        work_by_date: state => state.user_object?.auth?.company?.work_by_date || false,
        force_checklist_start: state => state.user_object?.auth?.company?.force_checklist_start || false,
        kitted_parts_behavior: state => state.user_object?.auth?.company?.kitted_parts_behavior || "always_kitted",
        // Normalised: `always_kitted` is the stored value of the disabled state
        // and reads as `off`, so every consumer compares against one vocabulary.
        kit_mode: state => kitMode(state.user_object?.auth?.company?.kitted_parts_behavior),
        kit_tracking_on: state => kitTrackingOn(state.user_object?.auth?.company?.kitted_parts_behavior),
        // Part batches drive the logistics viewer unless a company opts out
        // with meta.part_batch_flow = "off", which gives back the old viewer
        // where the ready counters are always editable.
        part_batch_flow: state => state.user_object?.auth?.company?.meta?.part_batch_flow || "on",
        part_batch_flow_on() { return this.part_batch_flow == "on" },
        // Returns true if the company has at least one of the given module flags enabled.
        // Usage: session_store.hasAnyModule(['m_logistics']) or ['m_bom', 'm_machines']
        hasAnyModule: (state) => (modules) => {
            const company = state.user_object.auth?.company
            if (!company) return false
            return modules.some(m => !!company[m])
        },

        // ---- ROLES ----------------------------------------------------
        //
        // The five roles that exist are company_admin, company_manager,
        // production_user, external_viewer and none, plus is_superuser and
        // pit_staff cutting across them. `external_deliverer` and `timer` are
        // properties on the model but are commented out of ROLES, so nothing
        // can be set to them and nothing here reads them.
        //
        // This is the same allowlist `onlyManagersGuard` uses. The left menu
        // used to ask `!iam_prod_worker` instead, which called every external
        // viewer and every no-rights account a manager and then let the router
        // turn them away with a red toast.
        iam_manager_or_admin: state => {
            const auth = state.user_object.auth || {}
            return !!(auth.company_admin || auth.company_manager || auth.is_superuser)
        },
        iam_external_viewer: state => !!state.user_object.auth?.external_viewer,

        // ---- INTERFACE MODES ------------------------------------------
        //
        // Which modes this account may enter at all, in workflow order:
        // material in, panels built, panels out.
        //
        // Only permission and module gates live here, because they hold still
        // for a whole session. Gates that move with the project selection are
        // `contextuallyDisabledModes` below: a button that vanishes and comes
        // back as you pick a project is worse than one that greys out.
        availableModes() {
            const modes = []
            if (this.iam_manager_or_admin) modes.push("manage")

            const m_logistics = this.hasAnyModule(['m_logistics'])
            const m_delivery = this.hasAnyModule(['m_delivery'])

            if (this.iam_prod_worker) {
                // A worker's line decides what they do. With no line selected
                // nothing narrows them, which is the case the old toggle
                // already handled this way.
                const no_line = !this.selected_line_uuid
                if (m_logistics && (no_line || this.pl_has_logistics_tipos))
                    modes.push("logistics")
                if (no_line || this.pl_has_production_tipos)
                    modes.push("production")
            } else if (this.iam_manager_or_admin) {
                if (m_logistics) modes.push("logistics")
                modes.push("production")
            } else if (this.iam_external_viewer) {
                // "Only view panels" — the production viewer and nothing else.
                modes.push("production")
            }

            // Delivery is deliberately NOT gated by production line tipos the
            // way logistics and production are. Loading a rack is not a station
            // on a line: it happens at the end of it or in the yard, done by
            // whoever is there. No line in the database carries two families of
            // tipos, so gating it that way would lock delivery to a dedicated
            // line nobody has.
            //
            // External viewers are left out on purpose: this mode is almost
            // entirely actions, and a read-only variant is a separate screen,
            // not a `:disabled` pass.
            if (m_delivery && (this.iam_manager_or_admin || this.iam_prod_worker))
                modes.push("delivery")

            return modes
        },

        // Modes the user owns but cannot use *right now*. Greyed, not hidden.
        contextuallyDisabledModes() {
            const selected_store = useSelectedStore()
            if (selected_store.stdlib_mode) return ["logistics", "production", "delivery"]
            // Company-wide only works for the productive modes when the company
            // works by date; otherwise there is no list to show.
            if (selected_store.pr_selection == "all" && !this.work_by_date)
                return ["logistics", "production", "delivery"]
            return []
        },

        canEnterMode() {
            return (mode) => this.availableModes.includes(mode)
        },
    },
    actions: {
        /** Replace the company nested in the session object.
         *
         * Every company setting is read from `user_object.auth.company`, which
         * arrives once with `/my_user/` and is never refetched. Saving a setting
         * writes the row and updates the company store, so the value was correct
         * in the database and stale in every getter here — the setting looked
         * like it had not saved until the next login.
         *
         * `patchCompany` calls this with the PUT response. Both payloads come
         * from `CompanySerializer`, so the shape matches.
         */
        syncCompany(company) {
            if (!company || !this.user_object?.auth) return
            this.user_object = {
                ...this.user_object,
                auth: { ...this.user_object.auth, company },
            }
        },

        setTokens(data) {
            this.access_token = data.access
            this.refresh_token = data.refresh
            this.authenticated = true
            localStorage.setItem('access_token', this.access_token)
            localStorage.setItem('refresh_token', this.refresh_token)
        },
        refreshToken(at) {
            this.access_token = at
            localStorage.setItem('access_token', this.access_token)
        },
        cleanSession() {
            this.access_token = ""
            this.refresh_token = ""
            this.authenticated = false
            localStorage.setItem('access_token', "")
            localStorage.setItem('refresh_token', "")
            Sentry.setUser(null)
        },
        setUserObject(data) {
            this.user_object = data
            Sentry.setUser({
                username: data.username,
            })
            Sentry.setTag('company', data.auth?.company?.name)
        },

        login(data) {
            const app_store = useAppStore()
            return backend_api_v2.post('/my_user/token/', data)
                .then(({ data }) => {
                    this.setTokens(data)
                    app_store.refreshBaseUserData(true)
                })
        },
        // Social login (Part A): exchange a Google/Microsoft id token for our tokens.
        loginWithOAuth(provider, id_token) {
            const app_store = useAppStore()
            return backend_api_v2.post('/my_user/token/oauth/', { provider, id_token })
                .then(({ data }) => {
                    this.setTokens(data)
                    app_store.refreshBaseUserData(true)
                })
        },
        // Account linking (Part C).
        getSocialIdentities() {
            return backend_api_v2.get('/my_user/social/')
                .then(({ data }) => data.providers)
        },
        linkSocial(provider, id_token) {
            return backend_api_v2.post('/my_user/social/', { provider, id_token })
        },
        unlinkSocial(provider) {
            return backend_api_v2.delete('/my_user/social/' + provider + '/')
        },
        // Resolves true when a new access token is in place. Shared: a view
        // fires a dozen requests at once, and when the token has expired every
        // one of them comes back 403 at the same moment. They all wait for the
        // same refresh instead of sending one each.
        refresh() {
            if (refreshing) return refreshing
            if (!this.refresh_token) {
                // "" means logged out; null means never logged in on this
                // device, and a public page (password reset) must stay put.
                if (this.refresh_token === "") bus.emit("events/session/logout")
                return Promise.resolve(false)
            }
            const app_store = useAppStore()
            console.log("[i] Refreshing access token")
            refreshing = backend_api_v2.post('/my_user/token/refresh/', { refresh: this.refresh_token })
                .then(({ data }) => {
                    this.refreshToken(data.access)
                    app_store.refreshBaseUserData(true)
                    return true
                })
                .catch((e) => {
                    // No answer at all is a lost connection, not a lost
                    // session: keep the tokens and let the next request try.
                    if (!e?.response) return false
                    // The response interceptor logs out on the 401 an expired
                    // refresh token gets.
                    bus.emit("notification", {
                        timeout: 3000,
                        text: "Failed to refresh session. You have to login.",
                        color: "error"
                    });
                    return false
                })
                .finally(() => { refreshing = null })
            return refreshing
        },
        getUserObject() {
            const translations_store = useTranslationsStore()
            return backend_api_v2.get('/my_user/')
                .then(({ data }) => {
                    this.setUserObject(data)
                    const locale = determineUserLanguage(data.auth)
                    vuetify.locale.current.value = locale
                    translations_store.loadLang(locale)
                })
                .catch(e => console.log(`Cant get User Object: ${e}`))
        },
        postUserSelectedStation(uuid, production_line_uuid = null) {
            return backend_api_v2.post('/my_user/select_station/', {
                selected_station_uuid: uuid,
                selected_production_line_uuid: production_line_uuid,
            })
                .then(({ data }) => {
                    this.setUserObject(data)
                })
                .catch(e => {
                    console.log(`Cant post user station: ${e}`)
                    throw e
                })
        },
        // The one way to pick a station and line: the Change Station dialog
        // and the viewer's refusal banner both go through here.
        selectStation(station_uuid, production_line_uuid = null) {
            const meta = { station: station_uuid }
            if (production_line_uuid) meta.production_line = production_line_uuid
            bus.emit("back_event", { event: "change_station", meta })
            return this.postUserSelectedStation(station_uuid, production_line_uuid)
                .catch(e => {
                    const translations_store = useTranslationsStore()
                    bus.emit("notification", {
                        text: translations_store.translate("switch_line_failed"),
                        color: "error",
                    })
                    throw e
                })
        },
        // Replaces the whole set: the dialog is a multi-select, so posting adds
        // and removes separately would only let the two sides disagree.
        postSelectedProductionEmployees(ids) {
            return backend_api_v2.post('/my_user/select_production_employees/', {
                employee_ids: ids,
            })
                .then(({ data }) => {
                    this.setUserObject(data)
                })
                .catch(e => {
                    console.log(`Cant post selected employees: ${e}`)
                    throw e
                })
        }
    },
})
